/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { onLine, RequestError, RequestFailureType } from '@amazon/vinyl-util'
import { GqlError } from '@/service/gql'

/**
 * Per-domain signals for {@link isKnownStubRejection}. Each caller (chat
 * feedback, card feedback, playback) recognizes a different set of
 * Firefly error codes / message fragments for "the id you sent isn't a
 * real entity", so the recognizable shapes are supplied by the caller
 * while the classification logic stays shared.
 */
export interface StubRejectionRules {
    readonly codes: ReadonlySet<string>
    readonly messageFragments: readonly string[]
}

/**
 * Returns true when a mutation rejection looks like the id we sent simply
 * isn't a real Firefly entity (dev-stub fixture / stale card id) rather
 * than a genuine, user-actionable failure. Detected via:
 *   1. `GqlError` with any `errors[].extensions.code` in `rules.codes`.
 *   2. `GqlError` whose error message contains a `rules.messageFragments`
 *      entry (case-insensitive).
 *   3. Any `GqlError` / `RequestError` that isn't a 5xx or network-layer
 *      failure — a 4xx / schema-level rejection Firefly returned as 200
 *      OK, which we can't attribute to a real server outage.
 *   4. Ambiguous errors while the user is *offline* — with no network we
 *      can't distinguish "gateway rejected the stub id" from "gateway is
 *      down", and yelling in the middle of an offline session is never
 *      useful.
 *
 * Loud errors are only shown when `onLine()` AND the
 * underlying failure is a 5xx / network layer error.
 */
export function isKnownStubRejection(err: unknown, rules: StubRejectionRules): boolean {
    if (err instanceof GqlError) {
        for (const gqlErr of err.errors) {
            const code = (gqlErr.extensions as { code?: unknown } | undefined)?.code
            if (typeof code === 'string' && rules.codes.has(code)) {
                return true
            }
            const message = typeof gqlErr.message === 'string' ? gqlErr.message.toLowerCase() : ''
            if (rules.messageFragments.some((fragment) => message.includes(fragment))) {
                return true
            }
        }
        return !isServerOrNetworkFailure(err)
    }
    if (err instanceof RequestError) {
        return !isServerOrNetworkFailure(err)
    }
    // Non-Request/Gql rejection (e.g. a plain Error thrown before the
    // fetch): suppress when offline; keep loud when online since we
    // can't classify.
    if (!onLine()) {
        return true
    }
    return false
}

/**
 * True when the rejection is definitively a 5xx response or a
 * network-transport failure — the only shapes we want to surface via the
 * shared error handler's "request failed" toast. `runGqlFetch` carries the
 * real transport status onto `GqlError.httpStatus` when it converts a
 * non-2xx `RequestError` response, so that case is checked directly rather
 * than falling through to the message heuristics above.
 */
function isServerOrNetworkFailure(err: unknown): boolean {
    if (!onLine()) {
        // Offline: can't confirm the server was actually reached.
        return false
    }
    if (err instanceof RequestError) {
        if (err.info.type === RequestFailureType.NETWORK) return true
        if (err.info.type === RequestFailureType.RESPONSE) {
            const status = err.info.responseInfo.status
            return status >= 500 && status < 600
        }
        return false
    }
    if (err instanceof GqlError && typeof err.httpStatus === 'number') {
        return err.httpStatus >= 500 && err.httpStatus < 600
    }
    return false
}
